import React, {
  FC,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from "@mui/material";
import useSound from "use-sound";

import {
  api,
  AssetLocation,
  AssetLocationTypes,
  ErrorToString,
  ExtendedLocationProperties,
  SourceApp,
} from "../../../logic/api";

import badSfx from "../../../assets/bad.mp3";
import busySfx from "../../../assets/busy.mp3";
import goodSfx from "../../../assets/good.mp3";
import swapSfx from "../../../assets/swap.mp3";

import flex from "../../../styles/flex";

import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import LocationPickerDialog from "../../../components/pickers/locationPicker/LocationPickerDialog";
import BarcodeScanner from "../../../components/thirdParty/react-barcode-reader";

import { SessionContext } from "../../../utility/useSession";

import styles from "./DynamicBoxMovements.module.css";
import { FiFolder } from "react-icons/fi";
import { useTranslation } from "react-i18next";

interface DynamicBoxMovementsProps {
  show: boolean;
  onClose: () => void;
}

const DynamicBoxMovements: FC<DynamicBoxMovementsProps> = ({
  show,
  onClose,
}) => {
  const session = useContext(SessionContext);
  const [scanned, setScanned] = useState<AssetLocation[]>([]);
  const [barcode, setBarcode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>();
  const getAssetLocationByCode = api.hooks.assetLocation.getByCode();
  const moveAssetLocation = api.hooks.assetLocation.move();
  const [location, setLocation] = useState<AssetLocation | null>(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const { t } = useTranslation();

  const [goodSound] = useSound(goodSfx);
  const [badSound] = useSound(badSfx);
  const [swapSound] = useSound(swapSfx);
  const [busySound] = useSound(busySfx);

  useEffect(() => {
    if (show) {
      setLocation(null);
      setErr(null);
      setMsg(null);
      setScanned([]);
      setBarcode("");
    }
  }, [show]);

  const switchLocation = (loc: AssetLocation | null) => {
    setShowLocationPicker(false);

    if (!loc) {
      return;
    }

    if (location != null) {
      if (location.id == loc.id) {
        setMsg(null);
        setErr(t("errors.locationAlreadySet", { location: loc.name }));
        badSound();
        return;
      }
    }

    setErr(null);
    setMsg(t("messages.locationChanged", { location: loc.name }));
    setLocation(loc);
    setScanned([]);
    swapSound();
  };

  const onBarcodeScanned = async (scannedBarcode: string) => {
    if (scanBusy) {
      busySound();
      return;
    }

    setScanBusy(true);
    await processScanned(scannedBarcode);
    setScanBusy(false);
  };

  const validateMovement = (result: AssetLocation, location: AssetLocation): string | undefined => {
    if (result.parentId === location.id) {
      const assignedType = location.type === AssetLocationTypes.Keyroom ? "keyroom" : "driver";
      return t("errors.boxAlreadyAssigned", { type: assignedType });
    }

    if (location.type == AssetLocationTypes.User) {
      if (result.parentType == AssetLocationTypes.User) {
        return `Cannot assign box to user as it is already with ${result.parentName}. Please assign back to key room first before assigning to user.`;
      }
    }

    if (location.type == AssetLocationTypes.Keyroom && result.parentType == AssetLocationTypes.Keyroom) {
      return `Cannot assign box to key room as it is already in ${result.parentName}.`;
    }

    return;
  }

  const processScanned = async (scannedBarcode: string) => {
    return new Promise<boolean>((accept) => {
      setErr(null);
      setMsg(null);

      const trimmedBarcode = (scannedBarcode ?? '').trim();
      if (trimmedBarcode.length === 0) {
        accept(true);
        return;
      }

      setBarcode("");

      getAssetLocationByCode.send(
        session.current.context,
        {
          code: trimmedBarcode,
          includeExtendedProperties: [ExtendedLocationProperties.parentType, ExtendedLocationProperties.keyCount],
        },
        (result) => {

          if (result) {
            const isResultEmpty = typeof result === 'object' && result != null && Object.keys(result).length === 0;

            if (!result || isResultEmpty) {
              setErr(t("errors.barcodeNotFound", { code: scannedBarcode }));
              badSound({ forceSoundEnabled: true });
              accept(true);
              return;
            }

            if (result.type !== AssetLocationTypes.DynamicBox) {
              switchLocation(result);
              accept(true);
              return;
            }

            if (!location) {
              setErr(t("errors.scanLocation"));
              badSound({ forceSoundEnabled: true });
              accept(true);
              return;
            }

            const errorMessage = validateMovement(result, location);
            if (errorMessage) {
              setErr(errorMessage);
              badSound({ forceSoundEnabled: true });
              accept(true);
              return;
            }

            moveAssetLocation.send(
              session.current.context,
              {
                id: result.id,
                to: location.id,
                sourceApp: SourceApp.frontEnd
              },
              (value) => {
                if (!value) {
                  setErr(t("errors.somethingWentWrong"));
                  badSound({ forceSoundEnabled: true });
                  accept(true);
                  return;
                }

                setMsg(`Dynamic box ${result.name} assigned to ${location.name}`);
                goodSound();
                scanned.push(result);
                accept(true);
              }
            );
          }
        }
      );
    });
  };

  return (
    <Fragment>
      <LocationPickerDialog
        title={t("keys.labels.selectLocation")}
        show={showLocationPicker}
        displayTypes={[
          AssetLocationTypes.Users,
          AssetLocationTypes.User,
          AssetLocationTypes.Keyroom,
        ]}
        selectionTypes={[
          AssetLocationTypes.User,
          AssetLocationTypes.Keyroom,
        ]}
        onChange={(loc) => switchLocation(loc)}
      ></LocationPickerDialog>
      <Dialog maxWidth={"md"} open={show}>
        <DialogTitle style={{ textAlign: "center", paddingBottom: 10 }}>
          <Fragment>
            <span style={{ color: "gray" }}>{t("keys.labels.dynamicBoxMovements")}</span>
          </Fragment>
        </DialogTitle>
        <DialogTitle style={{ textAlign: "left", paddingBottom: 10 }}>
          {location == null ? (
            <Fragment>
              <span style={{ color: "red" }}>{t("keys.labels.scanLocation")}</span>
            </Fragment>
          ) : (
            <Fragment>
              <b style={{ color: "red" }}>{location.name}</b>
            </Fragment>
          )}
        </DialogTitle>
        {location != null && (
          <DialogTitle
            style={{
              margin: 0,
              padding: 0,
              paddingLeft: 20,
              paddingRight: 20,
              paddingBottom: 10,
              textAlign: "center",
            }}
          >
            {scanned.length} {scanned.length !== 1 ? t("keys.labels.boxes") : t("keys.labels.box")} {t("keys.labels.scanned")}.
          </DialogTitle>
        )}
        <DialogContent style={{ minHeight: 50 }}>
          <div style={flex.flexRows}>
            <div style={flex.flexFill}>
              {scanBusy == false && (
                <BarcodeScanner
                  onError={(err: string) => {
                    console.log(err);
                  }}
                  onScan={(scan: string) => {
                    onBarcodeScanned(scan);
                  }}
                  minLength={1}
                />
              )}

              <TextField
                focused={true}
                inputProps={{
                  autoComplete: "off",
                  name: "input",
                }}
                style={{
                  width: "100%",
                }}
                inputRef={inputRef}
                type={"search"}
                size="small"
                label={t("expressCheckout.labels.barcode")}
                variant="outlined"
                value={barcode}
                onChange={(e) => {
                  setBarcode(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key == "Enter") {
                    onBarcodeScanned(barcode);
                  }
                }}
              />
            </div>
            <div style={flex.flexAuto}>
              <div style={{ marginLeft: 10 }}>
                <Button
                  onClick={() => {
                    setShowLocationPicker(true);
                  }}
                  variant="contained"
                  color={location == null ? "secondary" : "primary"}
                >
                  {location == null ? (
                    <Fragment>{t("keys.labels.selectLocation")}</Fragment>
                  ) : (
                    <Fragment>{t("keys.labels.changeLocation")}</Fragment>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogContent>
          <ProgressDialog status={getAssetLocationByCode.status}></ProgressDialog>

          <div style={{ width: 800 }}>
            {msg != null && (
              <div
                style={{
                  padding: 20,
                  marginBottom: 10,
                  backgroundColor: "lightgreen",
                  textAlign: "center",
                }}
              >
                <b>{msg.toUpperCase()}</b>
              </div>
            )}
            {err != null && (
              <div
                style={{
                  padding: 20,
                  marginBottom: 10,
                  backgroundColor: "pink",
                  textAlign: "center",
                }}
              >
                <b>{err.toUpperCase()}</b>
              </div>
            )}
            {getAssetLocationByCode.err != null && (
              <div
                style={{
                  padding: 20,
                  marginBottom: 10,
                  backgroundColor: "pink",
                  textAlign: "center",
                }}
              >
                <b>{ErrorToString(getAssetLocationByCode.err)}</b>
              </div>
            )}

            <div style={{ minHeight: 400 }}>
              {scanned.map((box, key) => {
                return (
                  <div key={key} className={styles.locationListItem}>
                    <div className={styles.locationListItem__icon}>
                      <FiFolder />
                    </div>
                    <div className={styles.locationListItem__name}>
                      {box.name} ({box.keyCount} keys)
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            <b>{t("general.done")}</b>
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default DynamicBoxMovements;
